import React, { useState } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import EOS from '../../agent/persona';
import { parseOpenUI } from '../../lib/openui';
import OpenUIRenderer from '../Demos/OpenUIRenderer';

// Keep native links so the visitor can open a source in another tab. Local
// routes use this site's hash router; react-markdown filters unsafe URLs first.
const replyComponents: Components = {
    a: ({ node, href, children, ...props }) => {
        if (!href) return <span>{children}</span>;
        const external = /^(?:https?:)?\/\//i.test(href);
        const destination = href.startsWith('/') && !href.startsWith('//') ? `#${href}` : href;
        return (
            <a {...props} href={destination} target={external ? '_blank' : undefined} rel={external ? 'noopener noreferrer' : undefined}>
                {children}
                {external && <><span aria-hidden="true"> ↗</span><span className="chatbot-panel__sr-only"> (opens in a new tab)</span></>}
            </a>
        );
    },
    pre: ({ node, children, ...props }) => {
        const firstChild = React.Children.toArray(children)[0];
        if (React.isValidElement<{ className?: string; children?: React.ReactNode }>(firstChild)) {
            const cls = firstChild.props.className || '';
            if (/language-openui(?:-lang)?\b/i.test(cls)) {
                const rawCode = String(firstChild.props.children ?? '').trim();
                const parsed = parseOpenUI(rawCode);
                if (parsed.root) {
                    return (
                        <div style={{ margin: '10px 0' }}>
                            <OpenUIRenderer node={parsed.root} compact />
                        </div>
                    );
                }
            }
        }
        return <pre {...props} className="chatbot-reply__pre" tabIndex={0} role="region" aria-label="Code block">{children}</pre>;
    },
    code: ({ node, className, ...props }) => <code {...props} className={`chatbot-reply__code${className ? ` ${className}` : ''}`} />,
    table: ({ node, ...props }) => (
        <div className="chatbot-reply__table-scroll" tabIndex={0} role="region" aria-label="Response table">
            <table {...props} />
        </div>
    ),
};

interface ChatReplyProps {
    text: string;
    onAction?: (label: string, payload?: string) => void;
}

const ChatReply: React.FC<ChatReplyProps> = ({ text, onAction }) => {
    const [copyState, setCopyState] = useState<'idle' | 'copying' | 'copied' | 'failed'>('idle');
    const components = React.useMemo<Components>(() => ({
        ...replyComponents,
        pre: ({ node, children, ...props }) => {
            const firstChild = React.Children.toArray(children)[0];
            if (React.isValidElement<{ className?: string; children?: React.ReactNode }>(firstChild)) {
                const cls = firstChild.props.className || '';
                if (/language-openui(?:-lang)?\b/i.test(cls)) {
                    const rawCode = String(firstChild.props.children ?? '').trim();
                    const parsed = parseOpenUI(rawCode);
                    if (parsed.root) {
                        return (
                            <div style={{ margin: '10px 0' }}>
                                <OpenUIRenderer node={parsed.root} onAction={onAction} compact />
                            </div>
                        );
                    }
                }
            }
            return <pre {...props} className="chatbot-reply__pre" tabIndex={0} role="region" aria-label="Code block">{children}</pre>;
        },
    }), [onAction]);

    const copyResponse = async () => {
        setCopyState('copying');
        try {
            if (typeof navigator.clipboard?.writeText !== 'function') throw new Error('Clipboard unavailable');
            await navigator.clipboard.writeText(text);
            setCopyState('copied');
        } catch {
            setCopyState('failed');
        }
    };

    return (
        <div className="chatbot-reply">
            <div className="chatbot-reply__header">
                <span className="chatbot-reply__label">{EOS.name}</span>
                <button type="button" className="chatbot-reply__copy" onClick={() => { void copyResponse(); }} disabled={copyState === 'copying'}>
                    Copy response
                </button>
                <span className="chatbot-reply__copy-status" role="status" aria-atomic="true">
                    {copyState === 'copied' ? 'Copied.' : copyState === 'failed' ? 'Copy failed. Select the text to copy it.' : ''}
                </span>
            </div>
            <div className="chatbot-reply__content">
                <ReactMarkdown components={components} remarkPlugins={[remarkGfm]} skipHtml>
                    {text}
                </ReactMarkdown>
            </div>
        </div>
    );
};

export default ChatReply;
